import React, { useState, useEffect } from 'react';
import { AdminLayout } from './AdminLayout';
import { auditLogsAPI } from '../../lib/api';
import { Loader2, History, User, FileText, Settings, Trash2, Plus, Edit, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { formatDateShort } from '../../lib/utils';

const ACTION_ICONS = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  publish: Eye,
  login: User,
  default: History
};

const ACTION_COLORS = {
  create: 'text-green-600 bg-green-100',
  update: 'text-blue-600 bg-blue-100',
  delete: 'text-red-600 bg-red-100',
  publish: 'text-purple-600 bg-purple-100',
  login: 'text-gray-600 bg-gray-100',
  default: 'text-gray-600 bg-gray-100'
};

export const AdminAuditLogs = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const limit = 30;

  useEffect(() => {
    fetchData();
  }, [page, actionFilter, resourceFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        ...(actionFilter !== 'all' && { action: actionFilter }),
        ...(resourceFilter !== 'all' && { resource_type: resourceFilter })
      };
      
      const [logsRes, statsRes] = await Promise.all([
        auditLogsAPI.getAll(params),
        auditLogsAPI.getStats(30)
      ]);
      
      setLogs(logsRes.data.logs);
      setTotal(logsRes.data.total);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    const baseAction = action.split('_')[0];
    const Icon = ACTION_ICONS[baseAction] || ACTION_ICONS.default;
    return Icon;
  };

  const getActionColor = (action) => {
    const baseAction = action.split('_')[0];
    return ACTION_COLORS[baseAction] || ACTION_COLORS.default;
  };

  if (loading && !logs.length) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-audit-logs">
        {/* Header */}
        <div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold">Audit Logs</h1>
          <p className="text-muted-foreground">Track all admin actions and changes</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.by_action && Object.entries(stats.by_action).slice(0, 4).map(([action, count]) => (
              <Card key={action}>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground capitalize">{action.replace('_', ' ')}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Most Active Users */}
        {stats?.by_user?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Most Active Users (Last 30 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {stats.by_user.map((user, index) => (
                  <div key={index} className="flex items-center gap-2 bg-secondary/50 px-3 py-2 rounded-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{user.user_email}</span>
                    <span className="text-sm font-bold">{user.actions}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="publish">Publish</SelectItem>
              <SelectItem value="login">Login</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Resource" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Resources</SelectItem>
              <SelectItem value="article">Articles</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="comment">Comments</SelectItem>
              <SelectItem value="settings">Settings</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logs List */}
        <Card>
          <CardHeader>
            <CardTitle>Activity Log ({total})</CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length > 0 ? (
              <>
                <div className="space-y-3">
                  {logs.map((log) => {
                    const Icon = getActionIcon(log.action);
                    const colorClass = getActionColor(log.action);
                    
                    return (
                      <div key={log.id} className="flex items-start gap-4 py-3 border-b border-border last:border-0">
                        <div className={`p-2 rounded-sm ${colorClass}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{log.user_email}</span>
                            <span className="text-muted-foreground">
                              {log.action.replace('_', ' ')} 
                              {log.resource_type && ` ${log.resource_type}`}
                            </span>
                          </div>
                          {log.resource_title && (
                            <p className="text-sm text-muted-foreground truncate">{log.resource_title}</p>
                          )}
                          {log.details && (
                            <p className="text-sm text-muted-foreground">{log.details}</p>
                          )}
                        </div>
                        <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                          {formatDateShort(log.timestamp)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {total > limit && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= total}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No audit logs found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminAuditLogs;
